import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Close as CloseIcon,
  PushPin as PinIcon,
} from '@mui/icons-material';
import { MinimalBirdIcon as BirdIcon } from './MinimalBirdIcon';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  DARK_THEME_EXPORTING,
  DARK_THEME_NAVIGATION,
  MOCK_DONUT_SPECIES_BREAKDOWN,
  DonutSpeciesItem,
} from '../bioacousticMockData';

interface ThreatDataPoint {
  name: string;
  count: number;
  color: string;
}

interface SpeciesDistributionDonutProps {
  data?: ThreatDataPoint[];
  title?: string;
  height?: number;
}

const DEFAULT_DATA: ThreatDataPoint[] = [
  { name: 'Endangered', count: 2, color: '#00D4FF' }, // cyan
  { name: 'Vulnerable', count: 4, color: '#8B5CF6' }, // purple
  { name: 'Least Concerned', count: 14, color: '#10B981' }, // emerald green
  { name: 'Unknown', count: 3, color: '#F97316' }, // orange
];

const ITEMS_PER_PAGE = 10;

const SpeciesDistributionDonut: React.FC<SpeciesDistributionDonutProps> = ({
  data = DEFAULT_DATA,
  title = 'Species Distribution by Threat Level (In-Situ)',
  height = 320,
}) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isFrozen, setIsFrozen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeCategoryData = data.find((d) => d.name === activeCategory) || data[0];

  // Get species list for active category matching the donut slice count
  const categorySpeciesList: DonutSpeciesItem[] = useMemo(() => {
    if (!activeCategory) return [];
    const fullList = MOCK_DONUT_SPECIES_BREAKDOWN[activeCategory] || MOCK_DONUT_SPECIES_BREAKDOWN['Least Concerned'] || [];
    const sliceCount = activeCategoryData?.count !== undefined ? activeCategoryData.count : fullList.length;
    return fullList.slice(0, sliceCount);
  }, [activeCategory, activeCategoryData]);

  const totalPages = Math.ceil(categorySpeciesList.length / ITEMS_PER_PAGE);

  const displayedSpecies = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return categorySpeciesList.slice(start, start + ITEMS_PER_PAGE);
  }, [categorySpeciesList, currentPage]);

  const handleSliceHover = useCallback((catName: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (!isFrozen) {
      setActiveCategory(catName);
      setCurrentPage(0);
    }
  }, [isFrozen]);

  const handleSliceClick = useCallback((catName: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (isFrozen && activeCategory === catName) {
      // Toggle off / unfreeze & close
      setIsFrozen(false);
      setActiveCategory(null);
    } else {
      // Freeze on this category
      setActiveCategory(catName);
      setIsFrozen(true);
      setCurrentPage(0);
    }
  }, [isFrozen, activeCategory]);

  const handleClosePopup = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsFrozen(false);
    setActiveCategory(null);
  };

  const handleContainerMouseLeave = () => {
    if (!isFrozen) {
      hoverTimeoutRef.current = setTimeout(() => {
        setActiveCategory(null);
      }, 300);
    }
  };

  const handlePopupMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePopupMouseLeave = () => {
    if (!isFrozen) {
      hoverTimeoutRef.current = setTimeout(() => {
        setActiveCategory(null);
      }, 300);
    }
  };

  const chartOptions: Highcharts.Options = useMemo(() => {
    return {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: height - 60,
        style: {
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
        },
      },
      exporting: DARK_THEME_EXPORTING,
      navigation: DARK_THEME_NAVIGATION,
      title: {
        text: '',
      },
      credits: { enabled: false },
      tooltip: {
        backgroundColor: '#111111',
        borderColor: '#222222',
        style: { color: '#FFFFFF', fontSize: '11px' },
        pointFormat: '<b>{point.name}</b>: {point.y} species ({point.percentage:.1f}%)<br/><span style="color:#10B981;font-size:10px;">Click to freeze/pin species catalog</span>',
      },
      plotOptions: {
        pie: {
          innerSize: '58%',
          depth: 20,
          allowPointSelect: true,
          cursor: 'pointer',
          point: {
            events: {
              mouseOver: function () {
                handleSliceHover(this.name);
              },
              click: function () {
                handleSliceClick(this.name);
              },
            },
          },
          dataLabels: {
            enabled: true,
            distance: -24,
            format: '{point.y}',
            style: {
              fontSize: '11px',
              fontWeight: '700',
              color: '#FFFFFF',
              textOutline: 'none',
            },
            filter: {
              property: 'y',
              operator: '>',
              value: 0,
            },
          },
          showInLegend: true,
          borderWidth: 1.5,
          borderColor: '#0A0A0A',
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          color: '#AAAAAA',
          fontSize: '10px',
          fontWeight: '500',
        },
        itemHoverStyle: {
          color: '#FFFFFF',
        },
        symbolRadius: 6,
      },
      series: [
        {
          type: 'pie',
          name: 'Species',
          data: data.map((d) => ({
            name: d.name,
            y: d.count,
            color: d.color,
            sliced: d.name === activeCategory,
            selected: d.name === activeCategory,
          })),
        },
      ],
    };
  }, [data, activeCategory, height, handleSliceHover, handleSliceClick]);

  return (
    <Box
      onMouseLeave={handleContainerMouseLeave}
      sx={{
        background: 'rgba(10, 10, 10, 0.85)',
        border: '1px solid #1E1E1E',
        borderRadius: '12px',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        minHeight: height,
        overflow: 'visible',
      }}
    >
      {/* Title Header */}
      <Box sx={{ mb: 0.5 }}>
        <Typography
          sx={{
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '700',
            letterSpacing: '0.5px',
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ fontSize: '11px', color: '#666666', mt: 0.25 }}>
          Hover to preview species • Click to freeze catalog
        </Typography>
      </Box>

      {/* Donut Chart (Remains 100% visible!) */}
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />

      {/* Floating Popover Card positioned adjacent to the Donut (Does NOT cover the chart) */}
      {Boolean(activeCategory) && (
        <Box
          onMouseEnter={handlePopupMouseEnter}
          onMouseLeave={handlePopupMouseLeave}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 'calc(100% + 14px)',
            transform: 'translateY(-50%)',
            width: '370px',
            maxHeight: '360px',
            zIndex: 100,
            background: 'rgba(13, 13, 13, 0.98)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${activeCategoryData?.color || '#282828'}88`,
            borderRadius: '12px',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.95), 0 0 24px rgba(0,0,0,0.85)',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeInSlide 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
            '@keyframes fadeInSlide': {
              '0%': { opacity: 0, transform: 'translateY(-50%) translateX(10px) scale(0.96)' },
              '100%': { opacity: 1, transform: 'translateY(-50%) translateX(0) scale(1)' },
            },
            // Sleek triangle indicator pointing towards the donut chart on the right
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              right: '-8px',
              transform: 'translateY(-50%)',
              borderWidth: '8px 0 8px 8px',
              borderStyle: 'solid',
              borderColor: `transparent transparent transparent ${activeCategoryData?.color || '#282828'}`,
            },
          }}
        >
          {/* Popover Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.25,
              pb: 1,
              borderBottom: '1px solid #222222',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: activeCategoryData?.color || '#10B981',
                  boxShadow: `0 0 10px ${activeCategoryData?.color || '#10B981'}`,
                }}
              />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                    {activeCategory}
                  </Typography>
                  {isFrozen && (
                    <Chip
                      icon={<PinIcon sx={{ fontSize: '10px !important', color: '#10B981' }} />}
                      label="Frozen"
                      size="small"
                      sx={{
                        height: '16px',
                        fontSize: '9px',
                        fontWeight: 700,
                        bgcolor: 'rgba(16,185,129,0.12)',
                        color: '#10B981',
                        border: '1px solid rgba(16,185,129,0.3)',
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: '10px', color: '#888', fontWeight: 600 }}>
                  {categorySpeciesList.length} Species Identified
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Pagination Controls if > 10 species */}
              {totalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    mr: 0.5,
                    bgcolor: '#1E1E1E',
                    px: 0.6,
                    py: 0.2,
                    borderRadius: '4px',
                    border: '1px solid #2A2A2A',
                  }}
                >
                  <Button
                    size="small"
                    disabled={currentPage === 0}
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    sx={{
                      minWidth: '20px',
                      p: 0,
                      color: activeCategoryData?.color || '#10B981',
                      '&.Mui-disabled': { color: '#444' },
                    }}
                  >
                    <PrevIcon sx={{ fontSize: 16 }} />
                  </Button>

                  <Typography sx={{ fontSize: '10px', color: '#AAA', fontWeight: 700, px: 0.5 }}>
                    {currentPage + 1}/{totalPages}
                  </Typography>

                  <Button
                    size="small"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    sx={{
                      minWidth: '20px',
                      p: 0,
                      color: activeCategoryData?.color || '#10B981',
                      '&.Mui-disabled': { color: '#444' },
                    }}
                  >
                    <NextIcon sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
              )}

              <Tooltip title={isFrozen ? 'Unfreeze / Close' : 'Close'}>
                <IconButton
                  size="small"
                  onClick={handleClosePopup}
                  sx={{ color: '#888', p: 0.5, '&:hover': { color: '#FFF', bgcolor: '#222' } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Paginated Species Grid List */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              maxHeight: 240,
              overflowY: 'auto',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': { background: '#2C2C2C', borderRadius: '2px' },
            }}
          >
            {displayedSpecies.length === 0 ? (
              <Typography sx={{ fontSize: '11px', color: '#666', textAlign: 'center', py: 2 }}>
                No species catalogued for this classification
              </Typography>
            ) : (
              displayedSpecies.map((sp, idx) => (
                <Box
                  key={sp.scientificName + idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#161616',
                    border: '1px solid #222222',
                    borderRadius: '6px',
                    px: 1.2,
                    py: 0.6,
                    '&:hover': {
                      borderColor: `${activeCategoryData?.color || '#10B981'}66`,
                      bgcolor: '#1B1B1B',
                    },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <BirdIcon sx={{ fontSize: 14, color: activeCategoryData?.color || '#10B981', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: '11px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.15 }}
                      >
                        {sp.name}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{ fontSize: '9.5px', color: '#777777', fontStyle: 'italic' }}
                      >
                        {sp.scientificName}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '10px', color: '#AAAAAA', fontWeight: 700 }}>
                      {sp.detections} calls
                    </Typography>
                    <Chip
                      label={`${sp.confidence}%`}
                      size="small"
                      sx={{
                        height: '17px',
                        fontSize: '9.5px',
                        fontWeight: 700,
                        bgcolor: 'rgba(16,185,129,0.12)',
                        color: '#10B981',
                        border: '1px solid rgba(16,185,129,0.28)',
                        px: 0.25,
                      }}
                    />
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SpeciesDistributionDonut;
